// Angular imports
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

// 3rd party imports
import { DxPopupComponent } from 'devextreme-angular';

// Service imports
import { UserPropertyResultDto } from '../../_models/user-property-result-dto';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { HelperService } from '../../_services/shared-services/helper.service';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-default-property',
  templateUrl: './default-property.component.html',
  styleUrls: ['./default-property.component.scss'],
  standalone: false
})
export class DefaultPropertyComponent implements OnInit, OnChanges {
  @ViewChild('propertyPopup', { static: false })
  propertyPopup: DxPopupComponent;

  @Input() userProperties: UserPropertyResultDto[];
  @Output() onSelection: EventEmitter<UserPropertyResultDto> =
    new EventEmitter();

  isSmallScreen: boolean = false;
  popupVisible: boolean = false;
  popupWidth: string | number = '100%';
  popupHeight: string = '500px';
  popupPosition: any = { my: 'top', at: 'top', of: window };

  selectedProperty: UserPropertyResultDto;
  selectedPropertyId: string = '';
  searchText: string = '';
  filteredProperties: UserPropertyResultDto[] = [];

  constructor(
    private screenService: ScreenService,
    private helperService: HelperService
  ) {}

  ngOnInit(): void {
    this.updateScreenSize();
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userProperties'] && this.popupVisible) {
      this.searchText = '';
      this.filteredProperties = this.userProperties
        ? [...this.userProperties]
        : [];
    }
  }

  show(): void {
    // Close the sidebar before showing the popup
    this.helperService.subjectCloseSideNav.next();

    this.searchText = '';
    this.filteredProperties = this.userProperties
      ? [...this.userProperties]
      : [];
    this.updatePopupPosition();
    document.body.classList.add('default-property-popup-open');
    this.popupVisible = true;
  }

  private updatePopupPosition(): void {
    const contentWrapper = document.querySelector('.content-wrapper');
    const headerHeight = contentWrapper
      ? contentWrapper.getBoundingClientRect().top
      : 56;

    if (this.isSmallScreen) {
      // On mobile, no sidebar - full width
      this.popupWidth = '100vw';
      this.popupPosition = {
        my: 'top left',
        at: 'top left',
        of: window,
        offset: `0 ${headerHeight}`
      };
    } else {
      // On desktop, account for collapsed sidebar (60px)
      const collapsedSidebarWidth = 60;
      this.popupWidth = `calc(100vw - ${collapsedSidebarWidth}px)`;
      this.popupPosition = {
        my: 'top left',
        at: 'top left',
        of: window,
        offset: `${collapsedSidebarWidth} ${headerHeight}`
      };
    }
  }

  hide(): void {
    document.body.classList.remove('default-property-popup-open');
    this.popupVisible = false;
  }

  onPopupShown(): void {
    this.propertyPopup.instance.repaint();
  }

  checkSelection(e: any): void {
    e.cancel = !this.selectedProperty;
  }

  onPropertyItemClick(property: UserPropertyResultDto): void {
    this.selectedProperty = property;
    this.selectedPropertyId = property.uniqueId;
    if (this.selectedProperty) {
      this.onSelection.emit(this.selectedProperty);
      this.hide();
    }
  }

  onSearchChanged(e: any): void {
    const searchValue = (e.value || '').toLowerCase();
    if (!this.userProperties) {
      this.filteredProperties = [];
      return;
    }
    if (!searchValue) {
      this.filteredProperties = [...this.userProperties];
    } else {
      this.filteredProperties = this.userProperties.filter(
        (property) =>
          property.communityName?.toLowerCase().includes(searchValue) ||
          property.propertyAddress?.toLowerCase().includes(searchValue) ||
          property.customerId?.toLowerCase().includes(searchValue)
      );
    }
  }

  onCloseClick(): void {
    if (this.selectedProperty) {
      this.hide();
    }
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
    if (this.popupVisible) {
      this.updatePopupPosition();
    }
  }
}
